import Editor from "@monaco-editor/react";
import { DataObjectOutlined, TokenOutlined, ViewAgendaOutlined } from "@mui/icons-material";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import ImageUploadIcon from "../../../assets/Fibo/icons/ImageUploadIcon";
import WandIcon from "../../../assets/Fibo/icons/WandIcon";
import WarningAlertIcon from "../../../assets/images/icons/WarningAlertIcon";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaInput from "../../../components/common/BriaInput/BriaInput";
import BriaTooltip from "../../../components/common/BriaTooltip/BriaTooltip";
import FileUploader from "../../../components/common/FileUploader/FileUploader";
import ImageUploader from "../../../components/common/ImageUploader/ImageUploader";
import StoppableButton from "../../../components/common/StoppableButton/StoppableButton";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlags.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { ThemeMode } from "../../../constants/themes";
import { useTheme } from "../../../context/ThemeContext";
import { useLdFeatureFlag } from "../../../hooks/useLdFeatureFlag.ts";
import { usePostHog } from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores";
import { getFiboLabVariant, isFibo2App, isFiboBbqApp } from "../../../utils";
import PromptTextToImageError from "../../Playground/TextToImage/Generator/Search/Error/PromptTextToImageError/PromptTextToImageError";
import FiboStructuredPrompt from "../StructuredPrompt/FiboStructuredPrompt";
import FiboAspectRatioButton from "./AspectRatio/FiboAspectRatioButton";
import styles from "./FiboConfig.module.scss";
import FiboModels from "./Models/FiboModels.tsx";
import FiboNegativePrompt from "./NegativePrompt/FiboNegativePrompt";
import FiboNumberOfImages from "./NumberOfImages/FiboNumberOfImages";
import FiboSeed from "./Seed/FiboSeed";
import FiboStepsButton from "./Steps/FiboStepsButton";
import FiboConfigTabs from "./Tabs/FiboConfigTabs";
import { useFiboConfig } from "./hooks/useFiboConfig/useFiboConfig.tsx";

type FiboConfigProps = {
	activeTab: "generate" | "refine";
	setActiveTab: (tab: "generate" | "refine") => void;
	isRefineTabEnabled: boolean;
};

const FiboConfig = ({ activeTab, setActiveTab, isRefineTabEnabled }: FiboConfigProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab" });
	const { textToImageStore, playgroundStore, fiboGenerateStore, fiboStore, authStore, anonymousUsageStore } =
		useAppStore();
	const { trackEvent } = usePostHog();
	const { mode: themeMode } = useTheme();
	const labVariant = getFiboLabVariant();
	const isFiboLiteFeatureEnabled = useLdFeatureFlag(FEATURE_FLAGS.FIBO_LITE_FEATURE);
	const isAnonymous = !authStore.isLoggedIn;
	const isBbq = isFiboBbqApp();
	const isFibo2 = isFibo2App();
	// FIBO 2 surfaces the structured caption as "VGL Output"; other variants keep "Structured Prompt".
	const spKey = isFibo2 ? "vglOutput" : "structuredPrompt";
	// Steps/Negative Prompt were removed from fibo and fibo-lite (BLD-3), but BBQ and FIBO 2 still
	// route through the same shared engine payload shape and need them exposed.
	const showStepsAndNegativePrompt = isBbq || isFibo2;
	const inputMode = fiboStore.inputMode;
	const jsonInput = fiboStore.jsonInput;
	const activeFeature =
		activeTab === "generate"
			? isBbq
				? "labs_bbq_generate"
				: "labs_generate"
			: isBbq
				? "labs_bbq_refine"
				: "labs_refine";
	const activeUsage = anonymousUsageStore.getUsage(activeFeature);

	const {
		isButtonDisabled,
		isDragging,
		handleDragEnter,
		handleDragLeave,
		handleDragOver,
		handleDrop,
		handlePaste,
		handleGenerateClick,
		handleRefineClick,
		handleSurpriseClick,
	} = useFiboConfig({ activeTab, isRefineTabEnabled });

	const hasImageReference = textToImageStore.imageReference?.file !== undefined;

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && labVariant) {
			trackEvent(POSTHOG_EVENTS.FIBO_IMAGE_UPLOADED, {
				lab_variant: labVariant,
				file_size: file.size,
				file_type: file.type,
				upload_type: "reference_image",
			});
		}
		await textToImageStore.handleUploadImage(e);
	};

	return (
		<Box className={styles.container}>
			{isFiboLiteFeatureEnabled && !isFiboBbqApp() && <FiboModels activeTab={activeTab} />}
			<FiboConfigTabs activeTab={activeTab} setActiveTab={setActiveTab} isRefineTabEnabled={isRefineTabEnabled} />

			{isFibo2 && (
				<Box className={styles.inputSectionHeader}>
					<Typography className={styles.inputSectionTitle}>{t("inputSection.title")}</Typography>
					<Select
						value={inputMode}
						onChange={(e) => fiboStore.setInputMode(e.target.value as "form" | "json")}
						size="small"
						variant="outlined"
						className={styles.inputModeSelect}
						renderValue={(value) => (
							<Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
								{value === "form" ? (
									<ViewAgendaOutlined sx={{ fontSize: 13 }} />
								) : (
									<DataObjectOutlined sx={{ fontSize: 13 }} />
								)}
								<span>{value === "form" ? t("inputSection.form") : t("inputSection.json")}</span>
							</Box>
						)}
					>
						<MenuItem value="form">{t("inputSection.form")}</MenuItem>
						<MenuItem value="json">{t("inputSection.json")}</MenuItem>
					</Select>
				</Box>
			)}

			{isFibo2 && inputMode === "json" ? (
				<Box className={styles.jsonEditorContainer}>
					<Editor
						height="100%"
						defaultLanguage="json"
						theme={
							themeMode === ThemeMode.Dark || themeMode === ("labs" as ThemeMode) ? "vs-dark" : "light"
						}
						value={jsonInput}
						onChange={(v) => fiboStore.setJsonInput(v ?? "{}")}
						options={{
							lineNumbers: "on",
							minimap: { enabled: false },
							wordWrap: "off",
							tabSize: 2,
							fontSize: 13,
							readOnly: false,
							lineDecorationsWidth: 0,
							glyphMargin: false,
							lineNumbersMinChars: 3,
							scrollBeyondLastLine: false,
						}}
					/>
				</Box>
			) : (
				<>
					<Box
						className={clsx(styles.inputCard, {
							[styles.dragging]: isDragging,
						})}
						onDragEnter={handleDragEnter}
						onDragLeave={handleDragLeave}
						onDragOver={handleDragOver}
						onDrop={handleDrop}
					>
						{isDragging && activeTab !== "refine" && (
							<Box className={styles.dragOverlay}>
								<Box className={styles.dragOverlayContent}>
									<ImageUploadIcon />
									<Typography className={styles.dragText}>{t("dragDropHere")}</Typography>
								</Box>
							</Box>
						)}
						<BriaInput
							multiline
							rows={4}
							placeholder={
								activeTab === "generate"
									? hasImageReference
										? t("prompt.imageReferencePlaceholder")
										: t("prompt.placeholder")
									: t("prompt.refinePlaceholder")
							}
							value={isFiboBbqApp() && hasImageReference ? "" : textToImageStore.config?.prompt}
							onChange={(e) => textToImageStore.handleConfigChange("prompt", e.target.value)}
							className={styles.textField}
							InputProps={{ className: styles.inputBase }}
							onEnterPress={activeTab === "generate" ? handleGenerateClick : handleRefineClick}
							onPaste={handlePaste}
							onFocus={() => fiboStore.structuredPromptStore.clearSelection()}
							disabled={isFiboBbqApp() && hasImageReference}
						/>

						<BriaButtonGroup className={styles.promptButtons}>
							{activeTab !== "refine" && (
								<BriaTooltip title={t("imageReferenceTooltip")}>
									<FileUploader
										buttonType="textSmall"
										buttonClassName={clsx(styles.iconButton, styles.imageUploadButton)}
										onUpload={handleImageUpload}
										buttonText={<ImageUploadIcon />}
										inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp" }}
									/>
								</BriaTooltip>
							)}
							<BriaTooltip title={t("surpriseIconTooltip")}>
								<BriaButton
									className={clsx(styles.iconButton, styles.surpriseButton)}
									title={t("surpriseIcon")}
									buttonType="textSmall"
									onClick={handleSurpriseClick}
								>
									<WandIcon />
								</BriaButton>
							</BriaTooltip>
						</BriaButtonGroup>
						{textToImageStore.errors.generate && (
							<PromptTextToImageError
								showDismissButton={false}
								icon={<WarningAlertIcon />}
								containerClassName={styles.promptTextToImageContainer}
								className={styles.promptTextToImageErrorContainer}
								warningContainerClassName={styles.promptTextToImageWarningContainer}
							/>
						)}

						{textToImageStore.imageReference?.url && (
							<ImageUploader
								src={textToImageStore.imageReference?.url}
								onUpload={handleImageUpload}
								onDelete={textToImageStore.handleDeleteImageReference}
								className={styles.imageUploader}
								showErrorOverlay={textToImageStore.imageReferenceContentModerationError}
							/>
						)}

						<Box className={styles.controlsRow}>
							<Box className={styles.buttonsContainer}>
								<FiboAspectRatioButton
									onChange={(newValue) =>
										textToImageStore.handleConfigChange("aspect_ratio", newValue)
									}
									value={textToImageStore.config?.aspect_ratio ?? "1:1"}
								/>
								{/* <GuidanceScale /> */}
								{showStepsAndNegativePrompt && <FiboStepsButton />}
								<FiboSeed
									onChange={(newValue) => textToImageStore.handleConfigChange("seed", newValue)}
									value={textToImageStore.config?.seed}
								/>
								{showStepsAndNegativePrompt && (
									<FiboNegativePrompt
										onChange={(newValue) =>
											textToImageStore.handleConfigChange("negative_prompt", newValue)
										}
										value={textToImageStore.config?.negative_prompt}
									/>
								)}
								{/* FIBO 2 always generates a single image, so the count selector is hidden */}
								{!isFibo2 && (
									<FiboNumberOfImages
										value={textToImageStore.config?.num_results}
										onChange={(newValue: number) =>
											textToImageStore.handleConfigChange("num_results", newValue)
										}
									/>
								)}
							</Box>

							{/* <BriaButton className={styles.updateButton} buttonType="textMedium">
								{t("structuredPrompt.updateButton")}
							</BriaButton> */}
						</Box>
					</Box>

					<FiboStructuredPrompt
						title={t(`${spKey}.title`)}
						infoTooltip={t(`${spKey}.infoTooltip`)}
						jsonSourceViewTooltip={t(`${spKey}.jsonSourceView`)}
						structuredViewTooltip={t(`${spKey}.structuredView`)}
						copyToClipboardTooltip={t(`${spKey}.copyToClipboard`)}
						loadingPlaceholderText={t(`${spKey}.loadingPlaceholderText`)}
						wordCountText={t(`${spKey}.wordCount`)}
						emptyStateTitle={t(`${spKey}.emptyState.title`)}
						emptyStateDescription={t(`${spKey}.emptyState.description`)}
						activeTab={activeTab}
					/>
				</>
			)}

			<BriaTooltip
				title={
					activeTab === "refine" && fiboStore.isSelectedGroupStructuredPromptModified()
						? t(isFibo2 ? "vglModifiedTooltip" : "jsonModifiedTooltip")
						: ""
				}
				placement="top"
				alwaysShown={activeTab === "refine" && fiboStore.isSelectedGroupStructuredPromptModified()}
				tooltipClassName={styles.generateButtonTooltip}
				arrowClassName={styles.generateButtonTooltipArrow}
			>
				<StoppableButton
					condition={
						textToImageStore.isGeneratingImages ||
						fiboStore.isGeneratingStructuredPrompt ||
						fiboStore.isPollingStatus ||
						playgroundStore.playgroundResults.some((result) =>
							result?.images?.some((image) => image?.loading),
						)
					}
					onStopButtonClick={() => {
						fiboGenerateStore.stopGenerate();
						if (labVariant) {
							trackEvent(POSTHOG_EVENTS.FIBO_STOP_GENERATION_CLICKED, {
								lab_variant: labVariant,
								active_tab: activeTab,
							});
						}
					}}
					onButtonClick={activeTab === "generate" ? handleGenerateClick : handleRefineClick}
					buttonLabel={
						<>
							{activeTab === "generate" ? t("generateButton") : t("refineButton")}
							{isAnonymous && (
								<span
									style={{
										marginLeft: 8,
										display: "inline-flex",
										alignItems: "center",
										gap: 4,
										fontSize: "0.85em",
										opacity: 0.9,
									}}
								>
									{activeUsage.used}/{activeUsage.limit}
									<TokenOutlined style={{ fontSize: "1.1em" }} />
								</span>
							)}
						</>
					}
					stopButtonLabel={activeTab === "generate" ? t("stopGenerate") : t("stopRefine")}
					disabled={isButtonDisabled}
					className={styles.actionButton}
				/>
			</BriaTooltip>
		</Box>
	);
};

export default observer(FiboConfig);
